import { useMemo } from "react";

import { Line } from "react-chartjs-2";

import { useUser } from "@components/user-context";
import { BackgroundPlugin } from "@features/orders-v2/chart-plugins/background-plugin";
import { useUniversalBenchmarksConfiguratorContext } from "@features/universal-benchmarks/configurator/hooks";
import {
  type GetOverviewMetricsReport_me_overviewMetricsReport,
  type SalesPageMetric,
} from "@north-beam/nb-client-side";

import {
  generateChartData,
  generateChartDataset,
  getChartOptions,
} from "./utils";

interface Props {
  metric: SalesPageMetric;
  data: GetOverviewMetricsReport_me_overviewMetricsReport;
}

export const BenchmarkRevChart = ({ metric, data }: Props) => {
  const { user } = useUser();
  const { adoptableBenchmarksMetrics, daysSelection } =
    useUniversalBenchmarksConfiguratorContext();

  const chartDataset = useMemo(() => {
    const chartData = generateChartData({
      data: data.rows[0],
      adoptableBenchmarksMetrics,
      metric,
      daysSelection,
      user,
    });
    return generateChartDataset(chartData);
  }, [data, metric, adoptableBenchmarksMetrics, daysSelection, user]);

  return (
    <div className="h-full flex flex-col gap-2">
      <div className="flex-1 overflow-hidden">
        <Line
          datasetIdKey="id"
          options={getChartOptions({
            code: user.currencies.currentCurrency,
          })}
          data={chartDataset}
          plugins={[BackgroundPlugin]}
        />
      </div>
      <div className="shrink-0 flex items-center justify-center gap-5 text-xs">
        <div className="flex items-center gap-1">
          <span className="inline-block w-3 h-3 rounded-full bg-v2-priority-2"></span>
          Without Benchmark
        </div>
        <div className="flex items-center gap-1">
          <span className="inline-block w-3 h-3 rounded-full bg-v2-priority-9"></span>
          With Benchmark
        </div>
      </div>
    </div>
  );
};
